/**
 * Twitter/X Pixel Event Tracking Utility
 * 
 * This utility provides a type-safe interface for tracking Twitter/X Pixel events.
 * All events respect user cookie consent preferences.
 */

// Twitter/X Pixel Configuration
// Update these values with your actual Twitter Ads account details
const TWITTER_PIXEL_ID = 'qudrp'; // Your Twitter Pixel ID
const TWITTER_SUBSCRIPTION_EVENT_ID = 'tw-qudrp-qudru'; // Your subscription conversion event ID
const TWITTER_SIGNUP_EVENT_ID = 'tw-qudrp-qudsc'; // Your sign-up conversion event ID
const TWITTER_LEAD_EVENT_ID = 'tw-qudrp-qu7tk'; // Your lead conversion event ID (keeping old format, update if you have new one)

// Declare Twitter Pixel types
declare global {
  interface Window {
    twq?: (...args: any[]) => void;
    _twq?: any;
  }
}

/**
 * Check if Twitter Pixel is available and consent is given
 */
export function isTwitterPixelEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check if Twitter Pixel is loaded
  if (typeof window.twq === 'undefined') return false;
  
  // Check if marketing cookies are accepted
  try {
    const preferences = localStorage.getItem('seoro_cookie_preferences');
    if (preferences) {
      const parsed = JSON.parse(preferences);
      return parsed.marketing === true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * Track a Twitter/X Pixel conversion event
 * @param eventId - Twitter event ID (e.g., 'tw-qudrp-qudru')
 * @param eventData - Optional event parameters
 */
export function trackTwitterPixelEvent(
  eventId: string,
  eventData?: {
    value?: number | null;
    currency?: string | null;
    status?: string | null;
    conversion_id?: string | null;
    email_address?: string | null;
    contents?: Array<{
      content_type?: string | null;
      content_id?: string | null;
      content_name?: string | null;
      content_price?: number | null;
      num_items?: number | null;
      content_group_id?: string | null;
    }> | null;
  }
): void {
  // Always check if pixel is loaded first
  if (typeof window.twq === 'undefined') {
    console.warn(`[Twitter Pixel] Cannot track ${eventId} - pixel not initialized`);
    return;
  }

  // Check consent for logging purposes
  const consentGiven = isTwitterPixelEnabled();
  
  // For critical conversion events, log for debugging
  const isCriticalEvent = eventId === TWITTER_SUBSCRIPTION_EVENT_ID || 
                          eventId === TWITTER_SIGNUP_EVENT_ID || 
                          eventId === TWITTER_LEAD_EVENT_ID;
  if (isCriticalEvent) {
    console.log(`[Twitter Pixel] 🎯 Tracking conversion event: ${eventId}`, {
      eventData,
      pixelLoaded: typeof window.twq !== 'undefined',
      consentGiven,
      note: consentGiven ? '✅ Consent given' : '⚠️ No consent, but firing anyway (Twitter handles consent)'
    });
  }

  try {
    // ALWAYS fire the event - Twitter Pixel handles consent internally
    // This ensures conversion events are tracked even without explicit cookie consent
    // (matching Meta Pixel behavior)
    if (eventData) {
      // For subscription events, ensure required parameters are present
      if (isCriticalEvent) {
        // Twitter requires 'value' and 'currency' for conversion events
        if (eventData.value === null || eventData.value === undefined || !eventData.currency) {
          console.error(`[Twitter Pixel] ❌ ${eventId} event missing required parameters:`, {
            hasValue: eventData.value !== null && eventData.value !== undefined,
            hasCurrency: !!eventData.currency,
            eventData
          });
        }
        
        // Ensure value is a valid number
        if (eventData.value !== null && eventData.value !== undefined && 
            (isNaN(Number(eventData.value)) || Number(eventData.value) <= 0)) {
          console.error(`[Twitter Pixel] ❌ ${eventId} event has invalid value:`, eventData.value);
        }
      }
      
      window.twq('event', eventId, eventData);
      if (isCriticalEvent) {
        console.log(`[Twitter Pixel] 📤 twq('event', '${eventId}',`, JSON.stringify(eventData, null, 2), ')');
        console.log(`[Twitter Pixel] 🔍 Check Network tab → filter "ads-twitter.com" to verify request was sent`);
      }
    } else {
      window.twq('event', eventId);
      if (isCriticalEvent) {
        console.log(`[Twitter Pixel] 📤 twq('event', '${eventId}')`);
      }
    }
    
    // Log success for critical events
    if (isCriticalEvent) {
      console.log(`[Twitter Pixel] ✅ ${eventId} event sent successfully`);
      console.log(`[Twitter Pixel] 💡 Verify in Twitter Ads Manager → Events Manager`);
    }
  } catch (error) {
    console.error(`[Twitter Pixel] ❌ Error tracking ${eventId}:`, error);
  }
}

/**
 * Track subscription conversion event
 * 
 * IMPORTANT: This should ONLY be called when payment is confirmed successful.
 * In this app, it's called when Polar subscription status becomes 'active' or 'trialing',
 * which only happens after Polar confirms successful payment processing.
 * 
 * DO NOT call this for:
 * - Checkout initiation (use trackInitiateCheckout instead)
 * - Pending payments
 * - Failed payments
 * - Renewals (only track new subscriptions)
 */
export function trackTwitterSubscription(
  subscriptionData: {
    value: number;
    currency: string;
    conversion_id?: string;
    email_address?: string;
  }
): void {
  // Ensure pixel is ready before tracking critical conversion event
  if (typeof window.twq === 'undefined') {
    console.error('[Twitter Pixel] ❌ Cannot track subscription - pixel not loaded');
    return;
  }

  // Ensure value is a valid number (Twitter requires numeric value > 0)
  const value = Number(subscriptionData.value);
  if (isNaN(value) || value <= 0) {
    console.error('[Twitter Pixel] ❌ Invalid subscription value:', subscriptionData.value);
    return;
  }

  // Twitter requires value and currency for conversion events
  const eventData = {
    value: value,
    currency: subscriptionData.currency.toUpperCase(), // Ensure uppercase (USD not usd)
    status: 'completed', // Subscription is completed/active
    conversion_id: subscriptionData.conversion_id || null,
    email_address: subscriptionData.email_address || null
  };

  console.log('[Twitter Pixel] 📝 Tracking subscription conversion with required parameters:', eventData);
  console.log('[Twitter Pixel] 🔍 Event details:', {
    eventId: TWITTER_SUBSCRIPTION_EVENT_ID,
    eventData: eventData,
    pixelLoaded: typeof window.twq !== 'undefined',
    timestamp: new Date().toISOString()
  });
  
  trackTwitterPixelEvent(TWITTER_SUBSCRIPTION_EVENT_ID, eventData);
  
  // Additional verification logging
  console.log('[Twitter Pixel] ✅ Subscribe event fired - check Network tab for requests to ads-twitter.com');
  console.log('[Twitter Pixel] 💡 Verify in Twitter Ads Manager → Events Manager → Test Events');
}

/**
 * Track checkout initiation event
 * This should be called when user starts the checkout process
 */
export function trackTwitterInitiateCheckout(checkoutData?: {
  value?: number;
  currency?: string;
}): void {
  // Twitter doesn't have a standard "InitiateCheckout" event like Meta
  // You can create a custom event in Twitter Ads Manager for this
  // For now, we'll skip this or you can add a custom event ID
  console.log('[Twitter Pixel] 💡 InitiateCheckout tracking not implemented - create custom event in Twitter Ads Manager if needed');
}

/**
 * Track user registration/signup
 * This should be called when a new user completes registration
 * 
 * IMPORTANT: This should ONLY be called when a user successfully signs up.
 * In this app, it's called when a new user completes Google OAuth sign-in.
 */
export function trackTwitterSignUp(signUpData: {
  email_address?: string | null;
  conversion_id?: string | null;
  status?: string | null;
}): void {
  // Ensure pixel is ready before tracking
  if (typeof window.twq === 'undefined') {
    console.error('[Twitter Pixel] ❌ Cannot track sign-up - pixel not loaded');
    return;
  }

  // Twitter sign-up event requires contents array structure
  // Based on the event structure provided: tw-qudrp-qudsc
  const eventData = {
    contents: [
      {
        content_type: 'signup',
        content_id: signUpData.conversion_id || null,
        content_name: 'User Registration',
        content_price: null,
        num_items: 1,
        content_group_id: null
      }
    ],
    status: signUpData.status || 'completed', // 'started' or 'completed'
    conversion_id: signUpData.conversion_id || null, // User ID for deduplication
    email_address: signUpData.email_address || null // User email address
  };

  console.log('[Twitter Pixel] 📝 Tracking sign-up conversion:', eventData);
  console.log('[Twitter Pixel] 🔍 Event details:', {
    eventId: TWITTER_SIGNUP_EVENT_ID,
    eventData: eventData,
    pixelLoaded: typeof window.twq !== 'undefined',
    timestamp: new Date().toISOString()
  });
  
  // Call twq directly with the correct structure for sign-up event
  try {
    window.twq('event', TWITTER_SIGNUP_EVENT_ID, eventData);
    console.log('[Twitter Pixel] ✅ Sign-up event fired - check Network tab for requests to ads-twitter.com');
    console.log('[Twitter Pixel] 💡 Verify in Twitter Ads Manager → Events Manager → Test Events');
  } catch (error) {
    console.error('[Twitter Pixel] ❌ Error tracking sign-up:', error);
  }
}

/**
 * Track a lead conversion event
 * This should be called when a user becomes a lead (e.g., submits a form, requests demo, etc.)
 */
export function trackTwitterLead(leadData: {
  email_address?: string | null;
  conversion_id?: string | null;
  status?: string | null;
}): void {
  // Ensure pixel is ready before tracking
  if (typeof window.twq === 'undefined') {
    console.error('[Twitter Pixel] ❌ Cannot track lead - pixel not loaded');
    return;
  }

  // Twitter lead event parameters
  const eventData = {
    status: leadData.status || 'completed',
    conversion_id: leadData.conversion_id || null,
    email_address: leadData.email_address || null
  };

  console.log('[Twitter Pixel] 📝 Tracking lead conversion:', eventData);
  trackTwitterPixelEvent(TWITTER_LEAD_EVENT_ID, eventData);
}

/**
 * Track page view (landing page views)
 * This should be called on page load to track landing page views
 */
export function trackTwitterPageView(): void {
  // Ensure pixel is ready
  if (typeof window.twq === 'undefined') {
    console.warn('[Twitter Pixel] Cannot track page view - pixel not initialized');
    return;
  }

  try {
    // Twitter Pixel automatically tracks page views when twq('config') is called
    // Site visits are tracked automatically - no explicit event needed
    // Landing page views might need a specific event ID if configured in Twitter Ads Manager
    // For now, page views are handled automatically by the base pixel initialization
    // This function is here for future use if a specific landing page view event is needed
    console.log('[Twitter Pixel] 📄 Page view tracked automatically by pixel');
  } catch (error) {
    console.error('[Twitter Pixel] ❌ Error tracking page view:', error);
  }
}

/**
 * Debug/Testing Utilities
 * These functions are exposed on window for easy testing in browser console
 */

/**
 * Clear all subscription tracking flags (useful for testing)
 * Run this in console: window.clearTwitterPixelTracking()
 */
export function clearTwitterTracking() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    console.warn('[Twitter Pixel] Cannot clear tracking - not in browser environment');
    return { success: false, error: 'Not in browser environment' };
  }

  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith('twitter_subscription_tracked_') || key.startsWith('twitter_signup_tracked_'))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => sessionStorage.removeItem(key));
  console.log(`[Twitter Pixel] 🧹 Cleared ${keysToRemove.length} tracking flag(s)`);
  
  return { 
    success: true, 
    cleared: keysToRemove.length,
    message: `Cleared ${keysToRemove.length} tracking flag(s)`
  };
}

/**
 * Manually trigger subscription event for testing
 * Run this in console: window.testTwitterPixelSubscription({ value: 87.90, currency: 'USD' })
 */
export function testTwitterSubscription(testData?: { value?: number; currency?: string }) {
  const value = testData?.value || 87.90;
  const currency = testData?.currency || 'USD';

  // Check if pixel is loaded
  if (typeof window.twq === 'undefined') {
    console.error('[Twitter Pixel] ❌ Cannot test - pixel not loaded!');
    return { success: false, error: 'Pixel not loaded' };
  }

  console.log('[Twitter Pixel] 🧪 TEST MODE - Manually triggering subscription conversion event');
  console.log('[Twitter Pixel] Event data:', { value, currency });
  
  trackTwitterSubscription({
    value: value,
    currency: currency
  });

  console.log('[Twitter Pixel] ✅ Test event sent. Check Twitter Ads Manager to verify.');
  console.log('[Twitter Pixel] 💡 You can also check Network tab for requests to ads-twitter.com');
  
  return { 
    success: true, 
    message: 'Subscription conversion event sent',
    value,
    currency
  };
}

/**
 * Manually trigger sign-up event for testing
 * Run this in console: window.testTwitterPixelSignUp({ email_address: '[email protected]', conversion_id: 'test-123' })
 */
export function testTwitterSignUp(testData?: { email_address?: string; conversion_id?: string }) {
  const email_address = testData?.email_address || '[email protected]';
  const conversion_id = testData?.conversion_id || `test-${Date.now()}`;

  // Check if pixel is loaded
  if (typeof window.twq === 'undefined') {
    console.error('[Twitter Pixel] ❌ Cannot test - pixel not loaded!');
    return { success: false, error: 'Pixel not loaded' };
  }

  console.log('[Twitter Pixel] 🧪 TEST MODE - Manually triggering sign-up conversion event');
  console.log('[Twitter Pixel] Event data:', { email_address, conversion_id });
  
  // Use the same structure as the actual tracking function
  const eventData = {
    contents: [
      {
        content_type: 'signup',
        content_id: conversion_id,
        content_name: 'User Registration',
        content_price: null,
        num_items: 1,
        content_group_id: null
      }
    ],
    status: 'completed',
    conversion_id: conversion_id,
    email_address: email_address
  };
  
  try {
    window.twq('event', TWITTER_SIGNUP_EVENT_ID, eventData);
    console.log('[Twitter Pixel] ✅ Test sign-up event sent with contents array structure');
  } catch (error) {
    console.error('[Twitter Pixel] ❌ Error sending test sign-up event:', error);
    return { success: false, error: String(error) };
  }

  console.log('[Twitter Pixel] ✅ Test sign-up event sent. Check Twitter Ads Manager to verify.');
  console.log('[Twitter Pixel] 💡 You can also check Network tab for requests to ads-twitter.com');
  
  return { 
    success: true, 
    message: 'Sign-up conversion event sent',
    email_address,
    conversion_id
  };
}

// Expose testing utilities on window for easy console access
if (typeof window !== 'undefined') {
  (window as any).clearTwitterPixelTracking = clearTwitterTracking;
  (window as any).testTwitterPixelSubscription = testTwitterSubscription;
  (window as any).testTwitterPixelSignUp = testTwitterSignUp;
  
  // Comprehensive pixel diagnostic tool
  (window as any).diagnoseTwitterPixel = () => {
    console.log('🔍 Twitter/X Pixel Diagnostic Tool');
    console.log('==================================\n');
    
    const diagnostics: any = {
      pixelId: TWITTER_PIXEL_ID,
      subscriptionEventId: TWITTER_SUBSCRIPTION_EVENT_ID,
      signupEventId: TWITTER_SIGNUP_EVENT_ID,
      pixelLoaded: typeof window.twq !== 'undefined',
      issues: [],
      recommendations: []
    };
    
    // 1. Check Pixel ID
    console.log('1. Pixel Configuration:');
    console.log(`   Pixel ID: ${TWITTER_PIXEL_ID}`);
    console.log(`   Subscription Event ID: ${TWITTER_SUBSCRIPTION_EVENT_ID}`);
    console.log(`   Sign-up Event ID: ${TWITTER_SIGNUP_EVENT_ID}`);
    console.log('   💡 Verify these match your Twitter Ads Manager\n');
    
    // 2. Check if pixel is loaded
    console.log('2. Pixel Status:');
    if (diagnostics.pixelLoaded) {
      console.log('   ✅ Pixel is loaded (twq exists)');
    } else {
      console.log('   ❌ Pixel NOT loaded (twq is undefined)');
      diagnostics.issues.push('Pixel script not loaded');
      diagnostics.recommendations.push('Check if marketing cookies are accepted');
    }
    console.log('');
    
    // 3. Check cookie consent
    console.log('3. Cookie Consent:');
    try {
      const preferences = localStorage.getItem('seoro_cookie_preferences');
      if (preferences) {
        const parsed = JSON.parse(preferences);
        console.log('   Marketing cookies:', parsed.marketing ? '✅ Accepted' : '❌ Not accepted');
        if (!parsed.marketing) {
          diagnostics.issues.push('Marketing cookies not accepted');
          diagnostics.recommendations.push('Accept marketing cookies in cookie banner');
        }
      } else {
        console.log('   ⚠️  No cookie preferences set');
      }
    } catch (e) {
      console.log('   ⚠️  Could not check cookie preferences');
    }
    console.log('');
    
    // 4. Test sending events
    console.log('4. Testing Event Sending:');
    if (diagnostics.pixelLoaded) {
      try {
        // Test subscription event
        const subscriptionTestData = { value: 87.90, currency: 'USD', status: 'completed' };
        window.twq('event', TWITTER_SUBSCRIPTION_EVENT_ID, subscriptionTestData);
        console.log('   ✅ Test subscription event sent');
        
        // Test sign-up event
        const signupTestData = { status: 'completed', conversion_id: 'test-123', email_address: '[email protected]' };
        window.twq('event', TWITTER_SIGNUP_EVENT_ID, signupTestData);
        console.log('   ✅ Test sign-up event sent');
        
        console.log('   💡 Check Network tab → filter "ads-twitter.com" to see the requests');
      } catch (e) {
        console.log('   ❌ Error sending test events:', e);
        diagnostics.issues.push('Error sending events');
      }
    } else {
      console.log('   ⏭️  Skipped (pixel not loaded)');
    }
    console.log('');
    
    // 5. Check for common issues
    console.log('5. Common Issues Check:');
    console.log('   a) Ad blockers: May block ads-twitter.com requests');
    console.log('   b) Browser privacy: May block third-party tracking');
    console.log('   c) Network issues: Check if ads-twitter.com is accessible');
    console.log('   d) Events Manager: Events may take time to appear in Twitter Ads Manager');
    console.log('');
    
    // 6. Recommendations
    console.log('6. Next Steps:');
    console.log(`   ✅ Verify Pixel ID in Twitter Ads Manager: ${TWITTER_PIXEL_ID}`);
    console.log(`   ✅ Verify Subscription Event ID: ${TWITTER_SUBSCRIPTION_EVENT_ID}`);
    console.log(`   ✅ Verify Sign-up Event ID: ${TWITTER_SIGNUP_EVENT_ID}`);
    console.log('   ✅ Check Network tab → filter "ads-twitter.com"');
    console.log('   ✅ Check Twitter Ads Manager → Events Manager');
    console.log('');
    
    if (diagnostics.issues.length > 0) {
      console.log('⚠️  Issues Found:');
      diagnostics.issues.forEach((issue: string) => console.log(`   - ${issue}`));
      console.log('');
      console.log('💡 Recommendations:');
      diagnostics.recommendations.forEach((rec: string) => console.log(`   - ${rec}`));
    } else {
      console.log('✅ No issues detected!');
      console.log('💡 If events still don\'t show, check Twitter Ads Manager configuration');
    }
    
    return diagnostics;
  };
}

